import { formatPlate, intakeCollage, operatorLine, shortHash } from "@szerviz/core";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { LANGUAGE_CODES } from "@szerviz/i18n";
import { ErrorText } from "../components/ErrorText";
import { ObjectionBox, PrintCollage, PrintIntakeExtras, shotTime, useExtrasRows, useShotLabel, type IntakeShot } from "../components/Intake";
import { WorkshopContactsBlock } from "../components/ContactLinks";
import { LanguageSelect } from "../components/LanguageSelect";
import { Logo } from "../components/Logo";
import { DocHead, KeyValues, MadeWith, Signatures, Stamp, useOperatorLabels } from "../components/PrintDocs";
import type { DataStore, PublicIntake } from "../data";
import { savedLanguage } from "../language";

/**
 * The customer's intake receipt page, opened from the e-mail of the workshop (#intake/<token>, feature
 * "intakePhotos"). No sign in: the secret token is the key, the link works 14 days like a quote link.
 * Phone first: the photos of the round, the facts of the receipt, a printable A4 receipt (PDF from the
 * browser), the workshop's phone number. Without a phone signature the customer can say "Nem értek egyet";
 * after a phone signature the page only informs. The language is the workshop's (like the quote page),
 * unless the customer chose another one here.
 */
export function IntakePage({ store, token }: { store: DataStore; token: string }) {
  const { t, i18n } = useTranslation();
  const opLabels = useOperatorLabels();
  const [view, setView] = useState<PublicIntake | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [open, setOpen] = useState<IntakeShot | null>(null);
  const [printing, setPrinting] = useState(false);
  const label = useShotLabel();
  const extrasRows = useExtrasRows();
  useEffect(() => {
    const before = () => flushSync(() => setPrinting(true));
    const after = () => setPrinting(false);
    globalThis.addEventListener?.("beforeprint", before);
    globalThis.addEventListener?.("afterprint", after);
    return () => {
      globalThis.removeEventListener?.("beforeprint", before);
      globalThis.removeEventListener?.("afterprint", after);
    };
  }, []);
  const load = async () => {
    try {
      const v = await store.intakeView(token);
      const lang = savedLanguage() ?? v.language;
      if (LANGUAGE_CODES.includes(lang) && lang !== i18n.language) {
        await i18n.changeLanguage(lang);
        if (typeof document !== "undefined") document.documentElement.lang = lang;
      }
      setView(v);
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!view) return <main className="quote-page intake-page">{error ? <ErrorText error={error} /> : <p>{t("common.loading")}</p>}</main>;
  // who runs the workshop, small under its name (the name once when it is the trade name)
  const legalLine = operatorLine(view.contacts?.legal, view.workshopName, opLabels);
  const dateTime = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "long", timeStyle: "short" }).format(new Date(iso));
  const day = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "long" }).format(new Date(iso));
  const plate = view.plateNumber ? formatPlate(view.plateNumber, view.plateCountry ?? "HU") : "";
  const km = view.odometerKm === null ? null : `${view.odometerKm.toLocaleString(i18n.language)} km`;
  const shots: IntakeShot[] = view.photos.map((p) => ({ id: p.id, view: p.view, url: p.url, createdAt: p.createdAt }));
  const collage = intakeCollage(shots);
  const sig = view.signature;
  const call = view.workshopPhone ? t("intakePage.call", { phone: view.workshopPhone }) : null;
  const print = () => {
    flushSync(() => setPrinting(true));
    globalThis.print?.();
  };

  return (
    <main className="quote-page intake-page">
      <header className="qp-head">
        <div className="qp-brand">
          {view.logoUrl && <img className="qp-logo" src={view.logoUrl} alt={t("logo.alt", { name: view.workshopName })} />}
          <strong className="qp-shop">
            {view.workshopName}
            <span className="qp-by">
              <Logo size={16} word={false} onDark /> {t("quote.page.by")}
            </span>
          </strong>
        </div>
        {view.workshopPhone && (
          <a className="button" href={`tel:${view.workshopPhone.replace(/[^+\d]/g, "")}`}>
            {t("quote.page.call", { phone: view.workshopPhone })}
          </a>
        )}
      </header>
      <h1>{t("intakePage.title")}</h1>
      <p className="qp-car">
        {plate && <strong>{plate}</strong>} {view.vehicle} · {view.number}
      </p>
      <div className="qp-letter">
        <p>
          <strong>{view.customerName ? t("intake.mail.greeting", { name: view.customerName }) : t("intake.mail.greetingNoName")}</strong>
        </p>
        <p>{t(shots.length > 0 ? "intakePage.intro" : "intakePage.introNoPhotos", { plate, date: dateTime(view.receivedAt) })}</p>
      </div>

      {shots.length > 0 && (
        <ul className="ip-photos">
          {shots.map((s) => (
            <li key={s.id}>
              <button type="button" className="ig-tile ig-done" onClick={() => setOpen(s)} aria-label={label(shots, s)}>
                <img src={s.url} alt="" />
                <time className="ig-time" dateTime={s.createdAt}>{shotTime(s.createdAt, i18n.language)}</time>
                <span className="ig-label">{label(shots, s)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <dl className="ip-facts">
        {km && (
          <div>
            <dt>{t("workOrder.odometerKm")}</dt>
            <dd className="mono">{km}</dd>
          </div>
        )}
        {view.customerRequest && (
          <div>
            <dt>{t("workOrder.customerRequest")}</dt>
            <dd className="pre">{view.customerRequest}</dd>
          </div>
        )}
        {view.expectedReadyAt && (
          <div>
            <dt>{t("workOrder.expectedReadyAt")}</dt>
            <dd>{day(view.expectedReadyAt)}</dd>
          </div>
        )}
        {extrasRows(view.intakeExtras).map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>

      {sig && (
        <p className="ok-band" role="status">
          {t("intakePage.signed", { name: sig.signerName, date: dateTime(sig.signedAt) })}
        </p>
      )}

      <button type="button" className="qp-print ip-pdf" onClick={print}>
        {t("intakePage.pdf")}
      </button>

      {!sig && (view.objection ? (
        <p className="ok-band" role="status">{t("intakePage.objectSent", { date: dateTime(view.objection.createdAt) })}</p>
      ) : (
        <ObjectionBox
          onSend={async (text) => {
            await store.intakeObject(token, text);
            await load();
          }}
        />
      ))}

      <footer className="qp-foot">
        {call && <p><strong>{call}</strong></p>}
        <p className="muted small">{t("intakePage.safe", { date: day(view.expiresAt) })}</p>
        <WorkshopContactsBlock contacts={view.contacts ?? { phone: view.workshopPhone, email: null, address: view.workshopAddress }} />
        <LanguageSelect />
        <p className="muted small">{view.workshopName}</p>
        {legalLine && <p className="muted small qp-legal">{legalLine}</p>}
      </footer>

      {open && (
        <div className="viewer" role="dialog" aria-modal="true" aria-label={label(shots, open)} onClick={() => setOpen(null)}>
          <div className="viewer-box" onClick={(e) => e.stopPropagation()}>
            <img src={open.url} alt={label(shots, open)} />
            <div className="viewer-bar">
              <strong>{label(shots, open)}</strong>
              <button type="button" className="primary" onClick={() => setOpen(null)}>
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* the receipt as an A4 sheet when printed or saved as PDF (only shown on paper) */}
      {printing && (
        <section className="print-sheet qp-sheet ps-doc-intake">
          <DocHead
            logo={view.logoUrl ? <img className="ps-logo" src={view.logoUrl} alt={t("logo.alt", { name: view.workshopName })} /> : null}
            shop={view.workshopName}
          legal={legalLine}
            lines={[[view.workshopAddress, view.workshopPhone].filter(Boolean).join(" · ")]}
            title={t("workOrder.print.openTitle")}
            number={view.number}
            meta={`${t("workOrder.print.receivedAt")}: ${dateTime(view.receivedAt)}`}
          />
          <div className="ps-boxes">
            <section>
              <p className="ps-cap">{t("workOrder.vehicle")}</p>
              <KeyValues rows={[[t("vehicle.plateNumber"), plate], [t("vehicle.model"), view.vehicle], [t("workOrder.odometerKm"), km, "mono"]]} />
            </section>
            <section>
              <p className="ps-cap">{t("workOrder.customer")}</p>
              <KeyValues rows={[[t("customer.name"), view.customerName]]} />
            </section>
          </div>
          {view.customerRequest && (
            <section className="ps-text">
              <p className="ps-cap">{t("workOrder.customerRequest")}</p>
              <p>{view.customerRequest}</p>
            </section>
          )}
          <section className="ps-ready">
            <div>
              <p className="ps-cap">{t("workOrder.expectedReadyAt")}</p>
              <div className="ps-big">{view.expectedReadyAt ? day(view.expectedReadyAt) : "–"}</div>
            </div>
          </section>
          <PrintIntakeExtras extras={view.intakeExtras} />
          {shots.length > 0 && (
            <PrintCollage shots={collage.shown} more={collage.more} caption={t("intake.print.caption", { count: shots.length, date: dateTime(shots[0]!.createdAt) })} />
          )}
          <div className="ps-stamp-row">
            <Stamp tone="orange" label={t("workOrder.print.stampIntake")} when={dateTime(view.receivedAt)} />
          </div>
          <div className="ps-bottom">
            <Signatures
              left={t("workOrder.print.signOrder")}
              right={t("workOrder.print.signReceiver")}
              leftAbove={sig && (
                <span className="ps-phone-sig">
                  {sig.url && <img src={sig.url} alt={t("signature.imageAlt", { name: sig.signerName })} />}
                  <small className="mono">{t("intake.print.signedOnPhone", { date: dateTime(sig.signedAt) })}</small>
                </span>
              )}
            />
            {sig && <p className="ps-legal ps-sig-note">{t("intake.print.hashNote", { hash: shortHash(sig.contentHash) })}</p>}
            <div className="ps-foot">
              <MadeWith />
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
